import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { PLANS } from "./Pricing";
import { useLang } from "../../i18n";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const inputCls = "w-full rounded-2xl border border-[#A69B8F]/40 bg-white px-5 py-4 text-base text-[#2D2D2D] placeholder:text-[#9a9087] outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#F26D5A] focus:shadow-[0_0_0_4px_rgba(242,109,90,0.15)]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-[#5a524a]">{label}</span>
    {children}
  </label>
);

const SizePicker = ({ value, onChange }) => (
  <div className="grid grid-cols-5 gap-2" role="radiogroup" data-testid="lead-size-select">
    {PLANS.map((p) => (
      <button
        type="button"
        key={p.id}
        role="radio"
        aria-checked={value === p.id}
        onClick={() => onChange(p.id)}
        data-testid={`lead-size-option-${p.id}`}
        className={`rounded-2xl border py-3 text-center transition-[background-color,border-color,color] duration-300 ${
          value === p.id ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#FDFBF7]" : "border-[#A69B8F]/40 bg-white text-[#2D2D2D] hover:border-[#F26D5A]"
        }`}
      >
        <span className="font-display block text-xl font-extrabold">{p.id}</span>
        <span className="block text-[11px] font-semibold opacity-80">{p.area} m²</span>
      </button>
    ))}
  </div>
);

export const LeadForm = ({ size, setSize, onLegal }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", consent: false, website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { t: all } = useLang();
  const t = all.lead;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!size) return toast.error(t.errSize);
    if (!form.consent) return toast.error(t.errConsent);
    if (!API) return toast.error(t.errOff);
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, size });
      setDone(true);
      toast.success(t.ok);
    } catch (err) {
      toast.error(t.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reserva" data-testid="lead-section" className="relative overflow-hidden bg-[#FDFBF7] py-24 sm:py-32">
      <div className="pointer-events-none absolute -left-40 bottom-0 h-[460px] w-[460px] rounded-full bg-[#F7D048]/30 blur-[120px]" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1fr] lg:items-start">
        <Reveal>
          <Eyebrow>{t.eyebrow}</Eyebrow>
          <h2 data-testid="lead-title" className="font-display mt-5 text-4xl font-extrabold leading-[1] tracking-tight text-[#2D2D2D] sm:text-5xl">
            {t.title[0]} <span className="block italic text-[#F26D5A]">{t.title[1]}</span>
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-[#5a524a] md:text-lg">
            {t.text}
          </p>
          <div className="relative mt-10 hidden aspect-[16/9] overflow-hidden rounded-[28px] lg:block">
            <img src="/images/unit.jpg" alt={t.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-[50%_60%]" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          {done ? (
            <div data-testid="lead-success" className="rounded-[28px] bg-[#2D2D2D] p-10 text-[#FDFBF7]">
              <CheckCircle2 className="h-12 w-12 text-[#F7D048]" />
              <h3 className="font-display mt-6 text-3xl font-extrabold">{t.doneTitle}</h3>
              <p className="mt-3 text-[#D9D2C8]">{t.doneText(size)}</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="lead-form" className="space-y-5 rounded-[28px] border border-[#A69B8F]/30 bg-[#F4F0E8] p-6 sm:p-9">
              <Field label={t.name}><input required minLength={2} value={form.name} onChange={set("name")} placeholder={t.namePh} autoComplete="name" className={inputCls} data-testid="lead-name-input" /></Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label={t.phone}><input required type="tel" minLength={6} value={form.phone} onChange={set("phone")} placeholder="6XX XXX XXX" autoComplete="tel" className={inputCls} data-testid="lead-phone-input" /></Field>
                <Field label={t.email}><input required type="email" value={form.email} onChange={set("email")} placeholder={t.emailPh} autoComplete="email" className={inputCls} data-testid="lead-email-input" /></Field>
              </div>
              <Field label={t.size}><SizePicker value={size} onChange={setSize} /></Field>
              <input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
              <label className="flex items-start gap-3 text-sm text-[#5a524a]">
                <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 h-5 w-5 accent-[#F26D5A]" data-testid="lead-consent-checkbox" />
                <span>{t.consent[0]} <button type="button" onClick={() => onLegal("privacy")} className="font-semibold text-[#2D2D2D] underline" data-testid="lead-privacy-link">{t.consent[1]}</button> {t.consent[2]}</span>
              </label>
              <button type="submit" disabled={loading} data-testid="lead-submit-btn" className="btn-magnet flex w-full items-center justify-center gap-3 rounded-full bg-[#F26D5A] px-6 py-4 text-base font-extrabold text-[#1f1f1f] disabled:opacity-70">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{t.submit} <ArrowRight className="h-5 w-5" /></>}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
